import { useEffect } from 'react';
import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';

export type SelectedCellInfo = TableSelectedCell;

export interface DetailRow {
  uuid: string | number;
  line?: number;
  so_luong?: number;
  gia_nt?: number;
  tien_nt?: number;
  thue_nt?: number;
  ma_thue_data?: {
    thue_suat?: number;
    [key: string]: MuaHangRecord;
  };
  ma_vt_data?: {
    gia_ton?: number;
    [key: string]: MuaHangRecord;
  };
  [key: string]: MuaHangRecord;
}

const roundToOneDecimal = (value: number) => Math.round((Number(value) || 0) * 10) / 10;

const toNumber = (value: MuaHangRecord) => Number(value) || 0;

const calculateRowValues = (row: DetailRow, taxRate?: number | null): Partial<DetailRow> => {
  const so_luong = toNumber(row.so_luong);
  const gia_nt = toNumber(row.gia_nt ?? row.ma_vt_data?.gia_ton);
  const tien_nt = roundToOneDecimal(so_luong * gia_nt);

  const numericTaxRate =
    taxRate !== undefined && taxRate !== null && !Number.isNaN(Number(taxRate))
      ? Number(taxRate)
      : toNumber(row.ma_thue_data?.thue_suat);

  const thue_nt = roundToOneDecimal((tien_nt * numericTaxRate) / 100);

  return {
    tien_nt,
    thue_nt
  };
};

const createFieldUpdates = (
  row: DetailRow,
  field: string,
  value: MuaHangRecord,
  taxRate?: number | null
): Partial<DetailRow> => {
  const updatedRow: DetailRow = { ...row, [field]: value };
  const so_luong = toNumber(field === 'so_luong' ? value : updatedRow.so_luong);
  const updates: Partial<DetailRow> = {};

  if (field === 'so_luong' || field === 'gia_nt') {
    const giaVon = toNumber(field === 'gia_nt' ? value : updatedRow.gia_nt);
    updates.tien_nt = roundToOneDecimal(so_luong * giaVon);
  }

  if (field === 'tien_nt') {
    const tien = toNumber(value);
    updates.tien_nt = tien;
    if (so_luong > 0) {
      updates.gia_nt = roundToOneDecimal(tien / so_luong);
    }
  }

  if (field === 'ma_vt_data' && value?.gia_ton !== undefined) {
    const giaVon = toNumber(value.gia_ton);
    updates.gia_nt = giaVon;
    updates.tien_nt = roundToOneDecimal(so_luong * giaVon);
  }

  const calculatedValues = calculateRowValues({ ...updatedRow, ...updates }, taxRate);

  return {
    ...updates,
    ...calculatedValues
  };
};

export function useDetailRows(
  initialRows: DetailRow[] = [],
  onChange?: (rows: DetailRow[]) => void,
  onFieldValueChange?: (row: DetailRow, field: string, value: MuaHangRecord) => Partial<DetailRow>,
  taxRate?: number | null
) {
  const handleFieldValueChange = (row: DetailRow, field: string, value: MuaHangRecord): Partial<DetailRow> => {
    const calculatedFields = createFieldUpdates(row, field, value, taxRate);
    const extraFields = onFieldValueChange?.(row, field, value) || {};

    return {
      ...calculatedFields,
      ...extraFields
    };
  };

  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleMoveRow,
    handlePasteRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<MuaHangRecord>({
    initialRows,
    defaultRows: 1,
    onChange,
    onFieldValueChange: handleFieldValueChange,
    defaultField: 'ma_vt'
  });

  useEffect(() => {
    if (taxRate === undefined || taxRate === null) return;
    setRows(prev =>
      prev.map(row => ({
        ...row,
        ...calculateRowValues(row, taxRate)
      }))
    );
  }, [taxRate, setRows]);

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
